<script setup lang="ts">
import { ProfileAdministrationAPI, ProfileContractorApi } from "../../api";
import { useToast } from "primevue/usetoast";
import { useConfirm } from "primevue/useconfirm";
import { computed, type PropType, ref } from "vue";
import { useI18n } from "vue-i18n";
import { Participant, Profile, Project } from "../../types/PersologOnlineTypes.ts";

const props = defineProps({
    participant: {
        type: Object as PropType<Participant>,
        required: true,
    },
});

const { t } = useI18n();
const confirm = useConfirm();

const loading = ref(false);

const participantName = computed(() => {
    const name = `${props.participant.firstName ?? ""} ${props.participant.name ?? ""}`.trim();
    return name || props.participant.email;
});

const confirmGroup = computed(() => `regenerate-participant-${props.participant.id}`);

/**
 * Regenerating discards the profiles that are still pending for this participant, so it asks first.
 */
const confirmRegenerate = () => {
    confirm.require({
        group: confirmGroup.value,
        header: t("messages.regenerate.confirmation.header"),
        message: t("messages.regenerate.confirmation.message", { participant: participantName.value }),
        icon: "pi pi-exclamation-triangle",
        rejectProps: {
            label: t("buttons.cancel"),
            severity: "secondary",
            outlined: true,
        },
        acceptProps: {
            label: t("buttons.ok"),
        },
        accept: () =>
            regenerateProfile(props.participant.id!, props.participant.questionnaire!, props.participant.projectId),
    });
};

const regenerateProfile = async (participantId: number, questionnaireHash: string, projectId: number) => {
    try {
        loading.value = true;
        const project: Project = await ProfileAdministrationAPI.fetchProject(projectId);
        const profile: Profile = {
            id: participantId,
            hash: questionnaireHash,
            language: project.language,
            product: project.product,
        };
        const profiles = await ProfileContractorApi.fetchPendingProfiles();
        const matchingProfiles = profiles.filter(
            (p) => p.id === profile.id && p.hash === profile.hash && p.language === profile.language && p.product === profile.product,
        );
        for (const p of matchingProfiles) {
            await ProfileContractorApi.deleteProfile(p);
        }
        await ProfileContractorApi.regenerateProfile(profile);
        showSuccessToast(participantId);
    } catch (error) {
        addToastError(participantId);
        console.error(error);
    } finally {
        loading.value = false;
    }
};

const toast = useToast();

const addToastError = (id: number) => {
    toast.add({
        severity: "error",
        summary: t("messages.error"),
        detail: t("messages.regenerate.error", { profile: id }),
        life: 4000,
    });
};

const showSuccessToast = (id: number) => {
    toast.add({
        severity: "success",
        summary: t("messages.success"),
        detail: t("messages.regenerate.success", { profile: id }),
        life: 3000,
    });
};
</script>

<template>
    <ConfirmDialog :group="confirmGroup" />
    <Button
        :data-cy="`regenerate-participant-button-${participant.id}`"
        icon="pi pi-replay"
        rounded
        outlined
        severity="secondary"
        class="mr-2"
        :aria-label="t('messages.regenerate.confirmation.header')"
        v-tooltip="t('messages.regenerate.confirmation.header')"
        :loading="loading"
        :disabled="participant?.status !== 'COMPLETED' && participant?.status !== 'GENERATED' && participant?.status !== 'NOTIFIED'"
        @click="confirmRegenerate"
    />
</template>
